import React, { useRef, useEffect, useState, useCallback } from 'react';
import * as fabric from 'fabric';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Undo2, Redo2, Save, Download, Share2,
  ChevronLeft, Settings2, DownloadCloud, Eye,
  FlipHorizontal, Shirt
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

import DesignToolbox from '../components/design/DesignToolbox';
import DesignProperties from '../components/design/DesignProperties';

const DesignEditor = () => {
  const canvasRef = useRef(null);
  const fabricRef = useRef(null);
  const [activeTool, setActiveTool] = useState('select');
  const [selectedObject, setSelectedObject] = useState(null);
  const [designName, setDesignName] = useState('Untitled Design');
  const [isSaving, setIsSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);
  const [buyingStatus, setBuyingStatus] = useState('idle'); // idle, processing, success
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [purchaseColor, setPurchaseColor] = useState({ name: 'Midnight Black', hex: '#111111' });
  const [purchaseSize, setPurchaseSize] = useState('M');
  const [purchaseComments, setPurchaseComments] = useState('');
  const [masterDesignFile, setMasterDesignFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [activeSellers, setActiveSellers] = useState([]);
  const [preferredSeller, setPreferredSeller] = useState('');

  // New States
  const [selectedGarment, setSelectedGarment] = useState(() => localStorage.getItem('design_garment') || 'tshirt');
  const [garmentView, setGarmentView] = useState('front');
  const [viewDesigns, setViewDesigns] = useState({ front: null, back: null });

  const GARMENTS = {
    tshirt: { name: 'T-Shirt', price: 1500, front: '/tshirt-front.png', back: '/tshirt-back.png' },
    shirt: { name: 'Shirt', price: 1500, front: '/shirtfront.png', back: '/shirtback.png' },
    sweatpants: { name: 'SweatPants', price: 1500, front: '/Sweatpantfront.png', back: '/Sweatpantback.png' },
    hoodie: { name: 'Hoodie', price: 1500, front: '/hoodiefront1.png', back: '/hoodieback1.png' },
    jeans: { name: 'Jeans', price: 1500, front: '/jeansfront.png', back: '/jeansback.png' },
    jacket: { name: 'Jacket', price: 1500, front: '/jacketfront.png', back: '/jacketback.png' },
    skirt: { name: 'Skirt', price: 1500, front: '/dress-front.png', back: '/dress-back.png' }
  };

  const TSHIRT_COLORS = [
    { name: 'Midnight Black', hex: '#111111' },
    { name: 'Classic White', hex: '#ffffff' },
    { name: 'Navy Blue', hex: '#1a2a5a' },
    { name: 'Heather Gray', hex: '#808080' },
    { name: 'Royal Red', hex: '#8b0000' }
  ];

  const TSHIRT_SIZES = ['S', 'M', 'L', 'XL', 'XXL'];



  const { cart, addToCart } = useCart();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const loadDesignId = searchParams.get('load');
  const [editingDesignId, setEditingDesignId] = useState(null);

  const saveHistory = useCallback(() => {
    if (!fabricRef.current) return;
    const json = JSON.stringify(fabricRef.current.toJSON());

    setHistory(prev => {
      const newHistory = prev.slice(0, historyIndex + 1);
      return [...newHistory, json];
    });
    setHistoryIndex(prev => prev + 1);
  }, [historyIndex]);

  // History Management stable callback
  const handleHistoryUpdate = useCallback(() => {
    saveHistory();
  }, [saveHistory]);

  const handleViewChange = async () => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    const newView = garmentView === 'front' ? 'back' : 'front';

    // Save current
    const currentJson = JSON.stringify(canvas.toJSON());
    const newDesigns = { ...viewDesigns, [garmentView]: currentJson };
    setViewDesigns(newDesigns);

    // Disable history during load
    canvas.off('object:added', handleHistoryUpdate);
    canvas.off('object:modified', handleHistoryUpdate);
    canvas.off('object:removed', handleHistoryUpdate);

    canvas.clear();

    if (newDesigns[newView]) {
      await canvas.loadFromJSON(newDesigns[newView]);
    }

    setGarmentView(newView);
    setHistory([]);
    setHistoryIndex(-1);

    canvas.on('object:added', handleHistoryUpdate);
    canvas.on('object:modified', handleHistoryUpdate);
    canvas.on('object:removed', handleHistoryUpdate);
    canvas.renderAll();
  };

  const handleGarmentChange = async (e) => {
    const newGarment = e.target.value;
    const canvas = fabricRef.current;
    if (!canvas) return;

    // Reset views on garment change
    setViewDesigns({ front: null, back: null });
    setGarmentView('front');
    setSelectedGarment(newGarment);
    canvas.clear();
    setHistory([]);
    setHistoryIndex(-1);
    canvas.renderAll();
  };

  // Background loading trigger
  const [isCanvasReady, setIsCanvasReady] = useState(false);

  // Fetch active sellers
  useEffect(() => {
    const fetchSellers = async () => {
      try {
        const res = await api.get('products/sellers/active');
        setActiveSellers(res.data.sellers || []);
      } catch (err) {
        console.error("Failed to fetch active sellers", err);
      }
    };
    fetchSellers();
  }, []);

  // Set Background Image
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas || !isCanvasReady) return;

    const loadBackground = async () => {
      try {
        const colorSuffix = purchaseColor?.name?.toLowerCase().split(' ').pop() || 'black';
        const baseImg = GARMENTS[selectedGarment][garmentView];
        // Construct URL: /tshirt-front.png -> /tshirt-front-black.png
        const bgUrl = baseImg.replace('.png', `-${colorSuffix}.png`);
        console.log("Loading background:", bgUrl);

        // Use FabricImage for v6+
        const bgImg = await fabric.FabricImage.fromURL(bgUrl, {
          crossOrigin: 'anonymous',
          selectable: false,
          evented: false,
          excludeFromExport: true
        });

        const scale = Math.min(canvas.width / bgImg.width, canvas.height / bgImg.height);
        bgImg.scale(scale);

        bgImg.set({
          left: canvas.width / 2,
          top: canvas.height / 2,
          originX: 'center',
          originY: 'center'
        });

        canvas.backgroundImage = bgImg;
        canvas.renderAll();
      } catch (err) {
        console.error("Failed to load background image:", err);
      }
    };

    loadBackground();
    localStorage.setItem('design_garment', selectedGarment);
  }, [selectedGarment, garmentView, isCanvasReady, purchaseColor]);

  // Load Design from Database Effect
  useEffect(() => {
    const fetchDesign = async () => {
      if (!loadDesignId || fabricRef.current?._loadingDesign === loadDesignId) return;

      setIsLoading(true);
      try {
        const res = await api.get(`designs/${loadDesignId}`);
        const design = res.data.design;

        setDesignName(design.name);
        setEditingDesignId(design._id);

        let parsedData;
        try {
          parsedData = JSON.parse(design.designData);
        } catch (e) {
          // Fallback for old simple JSON designs
          parsedData = { views: { [garmentView]: design.designData } };
        }

        if (parsedData.garment) setSelectedGarment(parsedData.garment);

        if (parsedData.views) {
          setViewDesigns(parsedData.views);

          // Load current view into canvas
          const canvas = fabricRef.current;
          if (canvas && parsedData.views[garmentView]) {
            // Mark as loading to prevent duplicate triggers
            canvas._loadingDesign = loadDesignId;
            await canvas.loadFromJSON(parsedData.views[garmentView]);
            canvas.renderAll();
          }
        }
      } catch (err) {
        console.error("Failed to load design:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDesign();
  }, [loadDesignId, garmentView]);


  // Initialize Canvas
  useEffect(() => {
    if (!canvasRef.current) return;

    const canvas = new fabric.Canvas(canvasRef.current, {
      width: 800,
      height: 600,
      backgroundColor: 'transparent',
      preserveObjectStacking: true,
    });

    fabricRef.current = canvas;
    setIsCanvasReady(true);

    // Selection Events
    canvas.on('selection:created', (e) => setSelectedObject(e.selected[0]));
    canvas.on('selection:updated', (e) => setSelectedObject(e.selected[0]));
    canvas.on('selection:cleared', () => setSelectedObject(null));

    // Optimized Eraser & Drawing Event Handlers
    let isDragging = false;

    canvas.on('mouse:down', (e) => {
      if (fabricRef.current?.__activeTool === 'eraser') {
        isDragging = true;
        const target = canvas.findTarget(e.e);
        // Specifically only erase drawings (paths)
        if (target && target.type === 'path') {
          canvas.remove(target);
          canvas.renderAll();
        }
      }
    });

    canvas.on('mouse:move', (e) => {
      if (isDragging && fabricRef.current?.__activeTool === 'eraser') {
        const target = canvas.findTarget(e.e);
        if (target && target.type === 'path') {
          canvas.remove(target);
          canvas.renderAll();
        }
      }
    });

    canvas.on('mouse:up', () => {
      isDragging = false;
    });

    const updateCanvasSize = () => {
      const container = canvasRef.current?.parentElement;
      if (!container) return;

      const width = container.offsetWidth;
      const height = container.offsetHeight;

      // Keep 1:1 or 4:3 aspect but fit in container
      const targetWidth = Math.min(width - 80, 800);
      const targetHeight = Math.min(height - 80, 600);

      canvas.setDimensions({ width: targetWidth, height: targetHeight });

      if (canvas.backgroundImage) {
        const bgImg = canvas.backgroundImage;
        // Use the original image dimensions for accurate scaling
        const originalWidth = bgImg._element ? bgImg._element.width : bgImg.width;
        const originalHeight = bgImg._element ? bgImg._element.height : bgImg.height;

        const scale = Math.min(targetWidth / originalWidth, targetHeight / originalHeight);
        bgImg.scale(scale);
        bgImg.set({
          left: targetWidth / 2,
          top: targetHeight / 2
        });
      }

      canvas.renderAll();
    };

    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);

    const handleKeyDown = (e) => {
      if (e.key === 'Backspace' || e.key === 'Delete') {
        const activeObject = canvas.getActiveObject();
        // Don't delete if we are in an input field
        if (activeObject && !['INPUT', 'TEXTAREA'].includes(document.activeElement.tagName)) {
          canvas.remove(activeObject);
          canvas.discardActiveObject();
          canvas.renderAll();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('resize', updateCanvasSize);
      window.removeEventListener('keydown', handleKeyDown);
      canvas.dispose();
      fabricRef.current = null;
    };
  }, []);

  // History Listeners Effect (Prevents stale closures)
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    canvas.on('object:added', handleHistoryUpdate);
    canvas.on('object:modified', handleHistoryUpdate);
    canvas.on('object:removed', handleHistoryUpdate);

    return () => {
      canvas.off('object:added', handleHistoryUpdate);
      canvas.off('object:modified', handleHistoryUpdate);
      canvas.off('object:removed', handleHistoryUpdate);
    };
  }, [handleHistoryUpdate]);



  const undo = async () => {
    if (historyIndex > 0 && fabricRef.current) {
      const prevIndex = historyIndex - 1;
      const json = history[prevIndex];

      const canvas = fabricRef.current;
      canvas.off('object:added', handleHistoryUpdate);
      canvas.off('object:modified', handleHistoryUpdate);
      canvas.off('object:removed', handleHistoryUpdate);

      await canvas.loadFromJSON(json);
      canvas.renderAll();
      setHistoryIndex(prevIndex);

      canvas.on('object:added', handleHistoryUpdate);
      canvas.on('object:modified', handleHistoryUpdate);
      canvas.on('object:removed', handleHistoryUpdate);
    }
  };

  const redo = async () => {
    if (historyIndex < history.length - 1 && fabricRef.current) {
      const nextIndex = historyIndex + 1;
      const json = history[nextIndex];
      const canvas = fabricRef.current;

      canvas.off('object:added', handleHistoryUpdate);
      canvas.off('object:modified', handleHistoryUpdate);
      canvas.off('object:removed', handleHistoryUpdate);

      await canvas.loadFromJSON(json);
      canvas.renderAll();
      setHistoryIndex(nextIndex);

      canvas.on('object:added', handleHistoryUpdate);
      canvas.on('object:modified', handleHistoryUpdate);
      canvas.on('object:removed', handleHistoryUpdate);
    }
  };

  // Tool Handlers
  const handleToolSelect = (toolId) => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    setActiveTool(toolId);

    switch (toolId) {
      case 'select':
        canvas.isDrawingMode = false;
        setActiveTool('select');
        canvas.__activeTool = 'select';
        break;
      case 'draw':
        canvas.isDrawingMode = true;
        canvas.freeDrawingBrush = new fabric.PencilBrush(canvas);
        canvas.freeDrawingBrush.width = 3;
        canvas.freeDrawingBrush.color = '#111111';
        setActiveTool('draw');
        canvas.__activeTool = 'draw';
        break;
      case 'eraser':
        canvas.isDrawingMode = false;
        setActiveTool('eraser');
        canvas.__activeTool = 'eraser';
        break;
      case 'image':
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = (e) => {
          const file = e.target.files[0];
          const reader = new FileReader();
          reader.onload = async (f) => {
            const img = await fabric.FabricImage.fromURL(f.target.result);
            img.scaleToWidth(200);
            canvas.add(img);
            canvas.setActiveObject(img);
            canvas.renderAll();
          };
          reader.readAsDataURL(file);
        };
        input.click();
        break;
      default:
        break;
    }
    canvas.renderAll();
  };

  const handleAction = async (actionId) => {
    const canvas = fabricRef.current;
    if (!canvas || !selectedObject) return;

    switch (actionId) {
      case 'duplicate':
        const cloned = await selectedObject.clone();
        cloned.set({
          left: selectedObject.left + 20,
          top: selectedObject.top + 20
        });
        canvas.add(cloned);
        canvas.setActiveObject(cloned);
        break;
      case 'front':
        canvas.bringObjectToFront(selectedObject);
        break;
      case 'forward':
        canvas.bringObjectForward(selectedObject);
        break;
      case 'backward':
        canvas.sendObjectBackwards(selectedObject);
        break;
      case 'back':
        canvas.sendObjectToBack(selectedObject);
        break;
      case 'delete':
        canvas.remove(selectedObject);
        setSelectedObject(null);
        break;
      default:
        break;
    }
    canvas.renderAll();
  };

  const handlePropertyChange = (property, value) => {
    if (!selectedObject) return;
    const canvas = fabricRef.current;

    if (['brightness', 'contrast', 'saturation', 'blur'].includes(property)) {
      if (!selectedObject.filters) selectedObject.filters = [];

      const filterMap = {
        'brightness': fabric.filters.Brightness,
        'contrast': fabric.filters.Contrast,
        'saturation': fabric.filters.Saturation,
        'blur': fabric.filters.Blur
      };

      const FilterClass = filterMap[property];
      const filterIndex = selectedObject.filters.findIndex(f => f instanceof FilterClass);
      const filterValue = { [property]: value };

      if (filterIndex > -1) {
        selectedObject.filters[filterIndex] = new FilterClass(filterValue);
      } else {
        selectedObject.filters.push(new FilterClass(filterValue));
      }
      selectedObject.applyFilters();
    } else {
      selectedObject.set(property, value);
    }
    canvas?.renderAll();
  };

  const saveDesign = async (silent = false) => {
    if (!fabricRef.current) return null;
    if (!silent) setIsSaving(true);
    try {
      // Get current view data
      const currentJson = JSON.stringify(fabricRef.current.toJSON());
      const allViews = { ...viewDesigns, [garmentView]: currentJson };

      // We save both views in the designData
      const designData = JSON.stringify({
        views: allViews,
        garment: selectedGarment
      });

      const thumbnail = fabricRef.current.toDataURL({
        format: 'png',
        quality: 0.8,
        multiplier: 0.5 // scaled down thumbnail
      });

      const response = await api.post('designs', {
        name: editingDesignId ? `${designName} (Refined)` : designName,
        designData,
        thumbnail,
        isPublic: false
      });

      if (!silent) alert('Design saved as a fresh copy in your portfolio! ✨');
      return response.data.design;
    } catch (err) {
      console.error(err);
      if (!silent) alert('Error saving design');
      return null;
    } finally {
      if (!silent) setIsSaving(false);
    }
  };


  const handleBuy = async () => {
    setBuyingStatus('processing');
    try {
      // 1. Save design and get ID
      const savedDesign = await saveDesign(true);
      if (!savedDesign) throw new Error('Failed to save design before purchase');

      // 2. High-res capture for printing
      const customThumbnail = fabricRef.current.toDataURL({
        format: 'png',
        quality: 1,
        multiplier: 2 // 2x resolution for printing
      });

      // 3. Add to Cart
      await addToCart(
        '69b6a14cc7f96d7f96bd5587', // REAL base product ID for Custom T-shirt
        1,
        purchaseSize || 'M',
        purchaseColor?.name || 'Black',
        true,
        savedDesign._id,
        customThumbnail,
        masterDesignFile,
        purchaseComments,
        preferredSeller
      );

      setBuyingStatus('success');
      setTimeout(() => {
        setShowBuyModal(false);
        navigate('/cart');
      }, 1500);
    } catch (err) {
      console.error(err);
      alert('Purchase failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setBuyingStatus('idle');
    }
  };

  const exportPNG = () => {
    if (!fabricRef.current) return;
    const dataURL = fabricRef.current.toDataURL({
      format: 'png',
      quality: 1
    });
    const link = document.createElement('a');
    link.download = `${designName}.png`;
    link.href = dataURL;
    link.click();
  };

  return (
    <div className="fixed inset-0 flex flex-col bg-lux-bg font-sans overflow-hidden">
      {/* Top Navbar */}
      <header className="h-16 bg-white border-b border-lux-border flex items-center justify-between px-4 lg:px-8 z-30 shadow-sm gap-4 overflow-x-auto">
        <div className="flex items-center gap-4 lg:gap-6 min-w-max">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 lg:gap-2 text-[10px] uppercase tracking-widest font-bold text-lux-secondary hover:text-lux-primary transition-colors"
          >
            <ChevronLeft size={16} />
            <span className="hidden lg:inline">Back</span>
          </button>
          <div className="h-6 w-px bg-lux-border hidden lg:block" />
          <div className="flex items-center gap-2 group bg-lux-bg rounded-lg px-2 py-1">
            <Settings2 size={14} className="text-lux-gold" />
            <input
              type="text"
              value={designName}
              onChange={(e) => setDesignName(e.target.value)}
              className="text-xs lg:text-sm font-medium text-lux-primary bg-transparent border-none focus:ring-0 focus:outline-none w-24 lg:w-48"
            />
          </div>

          <div className="h-6 w-px bg-lux-border" />

          <div className="flex items-center gap-2">
            <Shirt size={16} className="text-lux-secondary" />
            <select
              value={selectedGarment}
              onChange={handleGarmentChange}
              className="bg-transparent text-xs font-bold uppercase tracking-widest text-lux-primary border-none focus:ring-0 cursor-pointer appearance-none outline-none"
            >
              {Object.entries(GARMENTS).map(([key, g]) => (
                <option key={key} value={key}>{g.name}</option>
              ))}
            </select>
          </div>

          <div className="h-6 w-px bg-lux-border" />

          {/* Color Selection in Header */}
          <div className="flex items-center gap-2">
            {TSHIRT_COLORS.map(color => (
              <button
                key={color.name}
                onClick={() => setPurchaseColor(color)}
                className={`w-6 h-6 rounded-full border-2 transition-all ${purchaseColor?.name === color.name ? 'border-lux-gold scale-110' : 'border-white opacity-60 hover:opacity-100'}`}
                style={{ backgroundColor: color.hex }}
                title={color.name}
              />
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2 lg:gap-4 min-w-max">
          <button
            onClick={handleViewChange}
            className="flex items-center gap-2 px-3 lg:px-4 py-2 text-[10px] font-bold uppercase tracking-widest text-lux-secondary bg-lux-bg rounded-lg hover:bg-gray-200 transition-all"
          >
            <FlipHorizontal size={14} className={garmentView === 'back' ? 'scale-x-[-1]' : ''} />
            {garmentView === 'front' ? 'Front View' : 'Back View'}
          </button>

          <div className="flex bg-lux-bg rounded-lg p-1">
            <button
              onClick={undo}
              disabled={historyIndex <= 0}
              className="p-2 hover:bg-white rounded-md transition-all text-lux-secondary disabled:opacity-30 disabled:hover:bg-transparent"
              title="Undo"
            >
              <Undo2 size={18} />
            </button>
            <button
              onClick={redo}
              disabled={historyIndex >= history.length - 1}
              className="p-2 hover:bg-white rounded-md transition-all text-lux-secondary disabled:opacity-30 disabled:hover:bg-transparent"
              title="Redo"
            >
              <Redo2 size={18} />
            </button>
          </div>

          <div className="h-6 w-px bg-lux-border mx-2" />

          <div className="flex items-center gap-2">
            <button
              onClick={() => saveDesign()}
              disabled={isSaving}
              className="group flex items-center gap-2 px-5 py-2 text-[10px] font-bold uppercase tracking-widest text-lux-primary bg-white border border-lux-border rounded-lg hover:border-lux-primary transition-all shadow-sm active:scale-95"
            >
              {isSaving ? (
                <div className="w-4 h-4 border-2 border-lux-gold border-t-transparent rounded-full animate-spin" />
              ) : (
                <Save size={14} className="group-hover:text-lux-gold transition-colors" />
              )}
              Save Design
            </button>

            <button
              onClick={exportPNG}
              className="flex items-center gap-2 px-3 lg:px-5 py-2 text-[10px] font-bold uppercase tracking-widest text-lux-secondary bg-white border border-lux-border rounded-lg hover:bg-lux-bg transition-all active:scale-95 hidden sm:flex"
            >
              <Download size={14} />
              <span className="hidden lg:inline">Export</span>
            </button>
          </div>

          <div className="flex items-center gap-3 bg-white border border-lux-border rounded-lg p-1 pl-4 shadow-sm">
            <div className="flex flex-col">
              <span className="text-[8px] uppercase tracking-widest text-lux-secondary font-bold">Base Price</span>
              <span className="text-sm font-bold text-lux-primary">₹{GARMENTS[selectedGarment]?.price?.toLocaleString() || 0}</span>
            </div>
            <button
              onClick={() => setShowBuyModal(true)}
              disabled={buyingStatus !== 'idle'}
              className="flex items-center gap-3 px-6 py-2 text-[10px] font-bold uppercase tracking-[0.2em] text-white bg-lux-primary rounded-md hover:bg-lux-gold transition-all"
            >
              BUY NOW
            </button>
          </div>
        </div>
      </header>

      <div className="flex-grow flex overflow-hidden">
        {/* Left Toolbar */}
        <DesignToolbox
          activeTool={activeTool}
          onToolSelect={handleToolSelect}
          onAction={handleAction}
        />

        {/* Main Canvas Area */}
        <main
          className="flex-grow relative flex items-center justify-center p-12 overflow-auto bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:20px_20px]"
          style={{
            background: `
              radial-gradient(circle at center, rgba(17, 24, 39, 0.08) 0%, rgba(240, 242, 245, 0) 70%),
              radial-gradient(#e5e7eb 1px, transparent 1px),
              #F0F2F5
            `,
            backgroundSize: '100% 100%, 20px 20px, auto'
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="shadow-2xl rounded-sm overflow-hidden"
            style={{ width: 'fit-content' }}
          >
            <canvas ref={canvasRef} />
          </motion.div>



          {/* Zoom & Helper UI */}
          <div className="absolute bottom-8 right-8 flex flex-col gap-2">
            <div className="bg-white rounded-full shadow-lg p-2 flex items-center gap-4 px-4 border border-lux-border">
              <span className="text-[10px] uppercase tracking-widest font-bold text-lux-secondary">Canvas Size: 800x600</span>
              <div className="w-px h-4 bg-lux-border" />
              <Eye size={14} className="text-lux-gold" />
              <span className="text-[10px] font-bold text-lux-primary">100%</span>
            </div>
          </div>
        </main>

        {/* Right Properties Panel */}
        <DesignProperties
          selectedObject={selectedObject}
          onPropertyChange={handlePropertyChange}
        />
      </div>

      <AnimatePresence>
        {isSaving && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-lux-primary/20 backdrop-blur-sm z-50 flex items-center justify-center pointer-events-none"
          >
            <div className="bg-white p-8 rounded-2xl shadow-2xl flex flex-col items-center gap-4">
              <div className="w-12 h-12 border-4 border-lux-gold border-t-transparent rounded-full animate-spin" />
              <span className="text-xs uppercase tracking-widest font-semibold text-lux-primary">Saving Workspace...</span>
            </div>
          </motion.div>
        )}

        {showBuyModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-lux-primary/60 backdrop-blur-md z-50 flex items-center justify-center p-4 md:p-6"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              className="bg-white max-w-lg w-full rounded-3xl p-6 md:p-10 shadow-2xl overflow-y-auto max-h-[90vh]"
            >
              <div className="text-center mb-6 md:mb-8">
                <p className="text-lux-gold uppercase tracking-[0.3em] text-[10px] font-bold mb-2">Final Step</p>
                <h3 className="font-display text-2xl md:text-3xl text-lux-primary">Customize Your Order</h3>
                <p className="text-xs text-lux-secondary mt-2">Select your preferred color and size for the best fit</p>
              </div>

              {/* Color Selection Removed from here as per request */}

              {/* Size Selection */}
              <div className="mb-8 md:mb-10">
                <label className="block text-[10px] uppercase tracking-widest font-bold text-lux-primary mb-4 text-center">
                  Select Your Size <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-5 gap-2">
                  {TSHIRT_SIZES.map(size => (
                    <button
                      key={size}
                      onClick={() => setPurchaseSize(size)}
                      className={`py-3 md:py-4 rounded-xl border-2 text-xs font-bold transition-all ${purchaseSize === size ? 'border-lux-gold bg-lux-bg text-lux-gold shadow-inner' : 'border-lux-border text-lux-secondary hover:border-lux-gold/50'}`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>

              {/* Master Design Upload */}
              <div className="mb-8 p-6 bg-lux-bg rounded-2xl border border-lux-border border-dashed">
                <label className="block text-[10px] uppercase tracking-widest font-bold text-lux-primary mb-3 text-center">
                  Upload Master Design File (Optional)
                </label>
                <p className="text-[9px] text-lux-secondary text-center mb-4 uppercase tracking-tighter">PDF, SVG or High-Res PNG for artisan reference</p>
                <div className="flex justify-center">
                  <label className="cursor-pointer group">
                    <input
                      type="file"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;

                        const formData = new FormData();
                        formData.append('file', file);

                        setIsUploading(true);
                        try {
                          const res = await api.post('upload', formData, {
                            headers: { 'Content-Type': 'multipart/form-data' }
                          });
                          setMasterDesignFile(res.data.fileUrl);
                        } catch (err) {
                          alert("Upload failed: " + (err.response?.data?.message || err.message));
                        } finally {
                          setIsUploading(false);
                        }
                      }}
                    />
                    <div className={`px-6 py-2 rounded-lg border text-[10px] font-bold uppercase tracking-widest transition-all ${masterDesignFile ? 'bg-green-50 border-green-200 text-green-700' : 'bg-white border-lux-border text-lux-secondary group-hover:border-lux-primary group-hover:text-lux-primary'}`}>
                      {isUploading ? 'Uploading...' : masterDesignFile ? '✓ File Uploaded' : 'Choose File'}
                    </div>
                  </label>
                </div>
                {masterDesignFile && (
                  <p className="text-[8px] text-center mt-2 text-lux-gold font-medium truncate px-4">
                    {masterDesignFile.split('/').pop()}
                  </p>
                )}
              </div>

              {/* Comment Box */}
              <div className="mb-8">
                <label className="block text-[10px] uppercase tracking-widest font-bold text-lux-primary mb-3">
                  Additional Details / Comments
                </label>
                <textarea
                  value={purchaseComments}
                  onChange={(e) => setPurchaseComments(e.target.value)}
                  placeholder="e.g. Please place the logo 2 inches below the collar..."
                  className="w-full bg-lux-bg border border-lux-border rounded-xl p-4 text-xs font-sans focus:ring-1 focus:ring-lux-gold focus:outline-none resize-none"
                  rows={3}
                />
              </div>

              {/* Preferred Seller */}
              {activeSellers.length > 0 && (
                <div className="mb-8">
                  <label className="block text-[10px] uppercase tracking-widest font-bold text-lux-primary mb-3">
                    Preferred Fulfillment Partner (Optional)
                  </label>
                  <p className="text-[9px] text-lux-secondary mb-3 uppercase tracking-tighter">Select a specific seller to handle this custom design first</p>
                  <select
                    value={preferredSeller}
                    onChange={(e) => setPreferredSeller(e.target.value)}
                    className="w-full bg-lux-bg border border-lux-border rounded-xl p-4 text-xs font-sans focus:ring-1 focus:ring-lux-gold focus:outline-none appearance-none cursor-pointer"
                  >
                    <option value="">-- Let anyone fulfill it --</option>
                    {activeSellers.map(seller => (
                      <option key={seller._id} value={seller._id}>
                        {seller.storeName || `${seller.firstName} ${seller.lastName}`}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col gap-4">
                <button
                  onClick={handleBuy}
                  disabled={buyingStatus !== 'idle' || !purchaseColor || !purchaseSize}
                  className={`w-full py-4 rounded-xl text-xs font-bold uppercase tracking-[0.2em] transition-all flex items-center justify-center gap-3 ${(!purchaseColor || !purchaseSize) ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-lux-primary text-white hover:bg-lux-gold shadow-lg shadow-lux-primary/20 active:scale-[0.98]'}`}
                >
                  {buyingStatus === 'processing' ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Processing...
                    </>
                  ) : (
                    'Confirm and Add to Cart'
                  )}
                </button>

                {buyingStatus === 'idle' && (
                  <button
                    onClick={() => setShowBuyModal(false)}
                    className="text-[10px] uppercase tracking-widest font-bold text-lux-secondary hover:text-red-500 transition-colors py-2"
                  >
                    Cancel and Adjust Design
                  </button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DesignEditor;
